import { useState, useEffect } from "react";
import logo from "../../assets/logo.png";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import GoogleLogin from "../../components/GoogleLogin";
import * as Yup from "yup";
import { mixpanelTracker } from "../../utils/mixpanel-utility";

interface SignUpProps {
  nextStep: any;
  handleChange: any;
  formData: any;
  validateForm: any;
  errors: any;
  setGoogleError: (error: string) => void;
}
const SignUp: React.FC<SignUpProps> = ({
  nextStep,
  handleChange,
  formData,
  validateForm,
  errors,
  setGoogleError,
}) => {
  const [googleError, setInternalGoogleError] = useState("");

  // Password visibility toggle
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isRepeatPasswordVisible, setIsRepeatPasswordVisible] = useState(false);

  // Track page load when component mounts
  useEffect(() => {
    // Get the referrer URL
    const referrer = document.referrer;

    // Categorize the traffic source
    let trafficSource = "direct";
    let isFromMainWebsite = false;

    if (referrer) {
      // Check if the user came from the main website
      if (referrer.includes("superpower.social")) {
        trafficSource = "superpower_website";
        isFromMainWebsite = true;

        // You can further categorize specific pages from the main website
        if (referrer.includes("/pricing")) {
          trafficSource = "superpower_website_pricing";
        } else if (referrer.includes("/features")) {
          trafficSource = "superpower_website_features";
        }
      }
      // Check for other common sources
      else if (referrer.includes("google.com")) {
        trafficSource = "google";
      } else if (
        referrer.includes("facebook.com") ||
        referrer.includes("fb.com")
      ) {
        trafficSource = "facebook";
      } else if (referrer.includes("instagram.com")) {
        trafficSource = "instagram";
      } else if (
        referrer.includes("twitter.com") ||
        referrer.includes("x.com")
      ) {
        trafficSource = "twitter";
      } else if (referrer.includes("linkedin.com")) {
        trafficSource = "linkedin";
      } else {
        trafficSource = "other_website";
      }
    } else {
      // Check URL parameters for UTM sources if there's no referrer
      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get("utm_source");
      if (utmSource) {
        trafficSource = `utm_${utmSource}`;

        // Check if the UTM source indicates it came from the main website
        if (utmSource === "superpower_website" || utmSource === "superpower") {
          isFromMainWebsite = true;
        }
      }
    }

    // Track the signup page view with detailed referrer info
    mixpanelTracker.trackEvent("Signup Page Viewed", {
      traffic_source: trafficSource,
      full_referrer: referrer || "none",
      from_main_website: isFromMainWebsite,
      has_utm_params: window.location.search.includes("utm_"),
      timestamp: new Date().toISOString(),
      url_path: window.location.pathname,
      query_params: window.location.search,
    });
  }, []);

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
    mixpanelTracker.trackEvent("Toggle Password Visibility", {
      page: "signup",
      is_visible: !isPasswordVisible,
    });
  };

  const toggleRepeatPasswordVisibility = () => {
    setIsRepeatPasswordVisible(!isRepeatPasswordVisible);
    mixpanelTracker.trackEvent("Toggle Repeat Password Visibility", {
      page: "signup",
      is_visible: !isRepeatPasswordVisible,
    });
  };

  const isFormComplete =
    formData.firstName !== "" &&
    formData.lastName !== "" &&
    formData.email !== "" &&
    formData.password !== "" &&
    formData.repeatPassword !== "";

  const hasErrors = Object.values(errors).some((error) => error !== "");
  const buttonShouldBeYellow = isFormComplete && !hasErrors;

  const errMsg = (errorMsg: string) => {
    return <span className="text-red text-xs">{errorMsg}</span>;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Track form submission attempt
    mixpanelTracker.trackEvent("Signup Form Submitted", {
      has_errors: hasErrors,
      form_complete: isFormComplete,
      has_marketing_consent: formData.receiveEmails,
    });

    // Continue with form validation
    if (validateForm()) {
      // Track successful validation before proceeding
      mixpanelTracker.trackEvent("Signup Form Validated Successfully");
      nextStep();
    } else {
      // Track validation failure with error types
      const errorTypes = Object.keys(errors).filter(
        (key) => errors[key] !== ""
      );
      mixpanelTracker.trackEvent("Signup Form Validation Failed", {
        error_types: errorTypes,
      });
    }
  };

  const trackFieldChange =
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      // Track field interaction
      if (e.target.value.trim() !== formData[field]) {
        mixpanelTracker.trackEvent("Signup Field Changed", {
          field_name: field,
          has_value: e.target.value.trim().length > 0,
        });
      }

      // Call the original handleChange function
      handleChange(field)(e);
    };

  return (
    <div className="min-h-screen bg-grey flex flex-col md:flex-row">
      {/* Left side - Form */}
      <div className="w-full md:w-1/2 p-8 flex flex-col md:justify-center">
        <div className="max-w-md mx-auto">
          <img src={logo} className="h-8 mb-8" alt="Superpower Logo" />

          <h1 className="text-2xl font-bold mb-2">
            Never miss another comment
          </h1>
          <p className="text-lg mb-6">
            Automate and optimize social interactions for organic and paid.{" "}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex flex-col md:flex-row gap-2">
              <div className="flex-1">
                <input
                  className={`bg-white bg-opacity-5 rounded-3xl px-4 py-2 w-full
                    ${errors.firstName ? "border border-red" : ""}`}
                  type="text"
                  onChange={trackFieldChange("firstName")}
                  value={formData.firstName}
                  placeholder="First name"
                  required
                />
                {errors.firstName && (
                  <div className="mt-1">{errMsg(errors.firstName)}</div>
                )}
              </div>

              <div className="flex-1">
                <input
                  className={`bg-white bg-opacity-5 rounded-3xl px-4 py-2 w-full
                    ${errors.lastName ? "border border-red" : ""}`}
                  type="text"
                  onChange={trackFieldChange("lastName")}
                  value={formData.lastName}
                  placeholder="Last name"
                  required
                />
                {errors.lastName && (
                  <div className="mt-1">{errMsg(errors.lastName)}</div>
                )}
              </div>
            </div>

            <div>
              <input
                className={`bg-white bg-opacity-5 rounded-3xl px-4 py-2 w-full
                  ${
                    errors.email || errors.emailExists
                      ? "border border-red"
                      : ""
                  }`}
                type="email"
                onChange={trackFieldChange("email")}
                value={formData.email}
                placeholder="Email"
                required
              />
              {errors.email && (
                <div className="mt-1">{errMsg(errors.email)}</div>
              )}
              {errors.emailExists && (
                <div className="mt-1 text-red text-xs">
                  <span className="underline">
                    There's already an account with that email address
                  </span>
                  {" - "}
                  <Link
                    to="/login"
                    className="text-yellow hover:text-opacity-75"
                    onClick={() => {
                      mixpanelTracker.trackEvent("Signup to Login Redirect", {
                        reason: "email_exists",
                      });
                    }}
                  >
                    Login {">"}
                  </Link>
                </div>
              )}
            </div>

            <div>
              <div
                className={`flex flex-row items-center bg-white bg-opacity-5 rounded-3xl
                  ${errors.password ? "border border-red" : ""}`}
              >
                <input
                  className="flex-grow bg-transparent rounded-3xl px-4 py-2"
                  type={isPasswordVisible ? "text" : "password"}
                  onChange={trackFieldChange("password")}
                  value={formData.password}
                  placeholder="Password"
                  required
                />
                {isPasswordVisible ? (
                  <EyeIcon
                    className="w-6 h-6 mx-4 cursor-pointer"
                    onClick={togglePasswordVisibility}
                  />
                ) : (
                  <EyeSlashIcon
                    className="w-6 h-6 mx-4 cursor-pointer"
                    onClick={togglePasswordVisibility}
                  />
                )}
              </div>
              {errors.password && (
                <div className="mt-1">{errMsg(errors.password)}</div>
              )}
            </div>

            <div>
              <div
                className={`flex flex-row items-center bg-white bg-opacity-5 rounded-3xl
                  ${errors.repeatPassword ? "border border-red" : ""}`}
              >
                <input
                  className="flex-grow bg-transparent rounded-3xl px-4 py-2"
                  type={isRepeatPasswordVisible ? "text" : "password"}
                  onChange={trackFieldChange("repeatPassword")}
                  value={formData.repeatPassword}
                  placeholder="Repeat Password"
                  required
                />
                {isRepeatPasswordVisible ? (
                  <EyeIcon
                    className="w-6 h-6 mx-4 cursor-pointer"
                    onClick={toggleRepeatPasswordVisibility}
                  />
                ) : (
                  <EyeSlashIcon
                    className="w-6 h-6 mx-4 cursor-pointer"
                    onClick={toggleRepeatPasswordVisibility}
                  />
                )}
              </div>
              {errors.repeatPassword && (
                <div className="mt-1">{errMsg(errors.repeatPassword)}</div>
              )}
            </div>

            <button
              className={`rounded-3xl px-4 py-2 w-full ${
                buttonShouldBeYellow
                  ? `bg-yellow text-black hover:brightness-90`
                  : `bg-white text-black`
              }`}
              type="submit"
              onClick={() => {
                // Track CTA button click
                mixpanelTracker.trackEvent("Start Free Trial Button Clicked", {
                  form_complete: isFormComplete,
                  has_errors: hasErrors,
                });
              }}
            >
              Start Free Trial
            </button>

            <div className="flex items-center my-4">
              <div className="h-px bg-white bg-opacity-20 flex-grow"></div>
              <span className="px-4 text-white text-opacity-60 text-sm">
                or
              </span>
              <div className="h-px bg-white bg-opacity-20 flex-grow"></div>
            </div>

            {/* Google Login Component */}
            {googleError && (
              <div className="text-red text-xs text-center mb-2">
                {googleError}
              </div>
            )}

            <div className="h-[46px] flex justify-center">
              <GoogleLogin
                mode="signup"
                onError={(error) => {
                  setGoogleError(error);
                  mixpanelTracker.trackEvent("Google Signup Error", {
                    error_message: error,
                  });
                }}
              />
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-yellow underline hover:text-opacity-75"
                onClick={() => {
                  mixpanelTracker.trackEvent("Navigate to Login", {
                    from: "signup_page",
                    location: "bottom_link",
                  });
                }}
              >
                Already have an account? Log in
              </Link>
            </div>
          </form>
        </div>
        <div className="mt-auto py-4 text-center w-full">
          <span className="text-xs">
            By signing up for a Superpower account, you agree to our
            <a
              href="https://www.superpower.social/terms-of-service"
              className="hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {" "}
              Terms of Service
            </a>{" "}
            and{" "}
            <a
              href="https://www.superpower.social/privacy-policy"
              className="hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {" "}
              Privacy Policy
            </a>
          </span>
        </div>
      </div>

      {/* Right side - Image & Testimonial */}
      <div className="hidden md:block w-1/2 bg-grey relative">
        {/* Background overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent">
          <img
            src={
              "https://superpower-alpha.s3.us-east-1.amazonaws.com/default_images/468433420_122195901488228291_2078869254401920832_n.jpg"
            }
            className="w-fit h-full image-fit object-cover"
          />
        </div>

        {/* Testimonial overlay at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="bg-black bg-opacity-70 p-6 rounded-lg backdrop-blur-sm">
            <p className="text-lg font-medium mb-2">
              "Superpower has played a pivotal role in my growth as a creator.
              Since using Superpower, I've gained over half a million followers
              across platforms."
            </p>
            <div className="flex items-center mt-2">
              <div className="w-10 h-10 rounded-full bg-violet flex items-center justify-center text-white font-bold mr-3">
                <img
                  className="rounded-full"
                  src={
                    "https://superpower-alpha.s3.us-east-1.amazonaws.com/default_images/channels4_profile.jpg"
                  }
                />
              </div>
              <div>
                <p className="font-bold">Ricky Forbes</p>
                <p className="text-sm text-white text-opacity-70">
                  Pro Storm Chaser / Content Creator
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
